/**
 * New shoot — the smallest start: where you're filming (the airport(s) you're at, or somewhere else: a
 * camera site, ranch, beach or rooftop by your location or pasted coordinates) and the aircraft you're
 * filming. The name defaults from the subject / place + date; time window (multi-day too), time zone,
 * kind, frame rate, slate code and notes are under "More details" (and editable later in Plan →
 * Mission details). Everything else (Chase, cameras, places, crew) is added later from Shoot setup or
 * the live "+ ADD". A solo shooter needs nothing more.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import type { Airport, FrameRate, LatLon, MissionKind } from '@apos/shared';
import { Button, Chip, Field, Icon, IconButton, Segmented, Select, Spinner, TextArea, TextInput } from '../../components/ui';
import { Disclosure, Notice } from './pageParts';
import { api, errorMessage } from '../../lib/api';
import { serverNow } from '../../lib/clock';
import { fmt } from '../../lib/fmt';
import { addDays, browserTimeZone, daysBetween, utcToZoned, zonedWindow, zoneLooksWrongFor } from '../../lib/time';
import { AppHeader } from '../AppHeader';
import { MISSION_KINDS } from '../labels';
import { TimeZoneSelect } from '../TimeZoneSelect';
import { aircraftBody, blankAircraft, defaultMissionName, normalizeRegistration, parseLatLon } from '../../features/setup/setupModel';
import { getPhoneFix } from '../../features/setup/phoneFix';
import { useAccount } from '../../state/account';
import type { Organization } from '@apos/shared';

type PickedAirport = Pick<Airport, 'icao' | 'name' | 'lat' | 'lon' | 'city'>;
type Where = 'airport' | 'elsewhere';

const FRAME_RATES: FrameRate[] = ['23.976', '24', '25', '29.97', '29.97DF', '30', '48', '50', '59.94', '59.94DF', '60'];
export function NewMissionPage() {
  const nav = useNavigate();
  const init = useMemo(() => {
    // the next whole hour → +3 h (e.g. 18:47 → 19:00–22:00)
    const timezone = browserTimeZone();
    const start = Math.ceil(serverNow() / 3600_000) * 3600_000;
    const s = utcToZoned(start, timezone);
    const e = utcToZoned(start + 3 * 3600_000, timezone);
    return { timezone, at: start, date: s.date, start: s.time, end: e.time };
  }, []);

  const [name, setName] = useState('');
  const [kind, setKind] = useState<MissionKind>('flight_test');
  const [kindTouched, setKindTouched] = useState(false);
  const [date, setDate] = useState(init.date);
  /** Null = a single day (an end before the start runs past midnight); a later date = multi-day. */
  const [endDate, setEndDate] = useState<string | null>(null);
  const [startTime, setStartTime] = useState(init.start);
  const [endTime, setEndTime] = useState(init.end);
  const [timezone, setTimezone] = useState(init.timezone);
  const [frameRate, setFrameRate] = useState<FrameRate>('23.976');
  const [code, setCode] = useState('');
  const [notes, setNotes] = useState('');
  const [where, setWhere] = useState<Where>('airport');
  const [airports, setAirports] = useState<PickedAirport[]>([]);
  const [spot, setSpot] = useState<LatLon | null>(null);
  const [placeName, setPlaceName] = useState('');
  const [subject, setSubject] = useState<string | null>(null);
  const account = useAccount((s) => s.data);
  const orgs = account?.organizations ?? [];
  /** undefined = nothing picked yet: the account's only organization, otherwise personal (matches the server). */
  const [orgPick, setOrgPick] = useState<string | null | undefined>(undefined);
  const ownerOrg: Organization | null = orgPick === undefined ? (orgs.length === 1 ? orgs[0]! : null) : orgs.find((o) => o.id === orgPick) ?? null;
  const dataLine = ownerOrg
    ? `Aircraft data comes from ${ownerOrg.name}’s own key: an admin adds it under Organization → Data sources.`
    : 'Personal shoot: aircraft data comes from your own key, added in My settings.';
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const moreRef = useRef<HTMLDetailsElement>(null);

  const { start: windowStart, end: windowEnd } = zonedWindow(date, startTime, endDate, endTime, timezone);
  const multiDay = !!endDate && endDate !== date;
  const center: LatLon | null = where === 'elsewhere' ? (spot ?? airports[0] ?? null) : (airports[0] ?? null);
  /** The zone and the times stay as the user set them; until then they follow the place. */
  const [zoneTouched, setZoneTouched] = useState(false);
  const [timesTouched, setTimesTouched] = useState(false);
  useEffect(() => {
    if (zoneTouched || !center) return;
    const guess = zoneForPlace(center);
    if (!guess || guess === timezone) return;
    setTimezone(guess);
    if (!timesTouched) {
      // the same next-hour default, read on the shoot's own clock
      const s = utcToZoned(init.at, guess);
      const e = utcToZoned(init.at + 3 * 3600_000, guess);
      setDate(s.date);
      setStartTime(s.time);
      setEndTime(e.time);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.lat, center?.lon]);
  const zoneHint = center && zoneLooksWrongFor(center.lon, timezone, windowStart);
  const fallbackName = defaultMissionName(subject || placeName.trim() || null, date);

  const changeDate = (d: string) => {
    // a multi-day window keeps its length when the first day moves
    if (endDate && d) setEndDate(addDays(endDate, daysBetween(date, d)));
    setDate(d);
  };
  const changeWhere = (w: Where) => {
    setWhere(w);
    setError(null);
    if (!kindTouched) setKind(w === 'elsewhere' ? 'remote' : 'flight_test');
  };
  const showMore = () => {
    if (moreRef.current) moreRef.current.open = true;
    moreRef.current?.querySelector<HTMLSelectElement>('select[data-tz]')?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!center) {
      setError(where === 'airport' ? 'Choose the airport you’re at (search ICAO, name or city) — or pick “Somewhere else”.' : 'Set where the shoot is: use your location, paste coordinates, or choose a nearby airport.');
      return;
    }
    if (!(windowEnd > windowStart)) {
      setError('The shoot has to end after it starts — check the end date and time under More details.');
      showMore();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const bundle = await api.missions.create({
        name: name.trim() || fallbackName,
        kind,
        windowStart,
        windowEnd,
        timezone,
        center: { lat: center.lat, lon: center.lon },
        notes: notes.trim(),
        frameRate,
        code: code.trim() || undefined,
        ...(orgs.length ? { organizationId: ownerOrg ? ownerOrg.id : null } : {}),
      });
      const id = bundle.mission.id;
      // at an airport: the first is the main one (weather), more are where we are too; somewhere else:
      // an airport is where the aircraft flies from / to (still weather). Best-effort.
      if (airports.length)
        await api.missionAirports.set(id, airports.map((a, i) => ({ icao: a.icao, role: where === 'elsewhere' ? 'alternate' : i === 0 ? 'primary' : 'nearby' }))).catch(() => undefined);
      if (where === 'elsewhere' && spot)
        await api.locations
          .create(id, { name: (placeName.trim() || 'SHOOT SITE').toUpperCase().slice(0, 60), kind: 'poi', lat: spot.lat, lon: spot.lon, elevM: null, radiusM: null, notes: '' })
          .catch(() => undefined);
      if (subject) await api.aircraft.create(id, aircraftBody({ ...blankAircraft([]), registration: subject })).catch(() => undefined);
      nav(`/m/${bundle.mission.id}/plan`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <AppHeader back={{ to: '/missions', label: 'Back to shoots' }} />
      <main className="page__main page__main--narrow">
        <div className="page__head">
          <div>
            <div className="eyebrow">New shoot</div>
            <h1>Plan a shoot</h1>
            <p className="page__lede">Where you’re filming and what you’re filming is enough to start. Add cameras, places and crew later.</p>
          </div>
        </div>
        <form className="form-card" onSubmit={submit}>
          <fieldset className="form-section">
            <legend className="eyebrow">Where are you filming?</legend>
            <Segmented<Where>
              block
              label="Where are you filming?"
              value={where}
              onChange={changeWhere}
              options={[
                { value: 'airport', label: 'At an airport', icon: 'aircraft' },
                { value: 'elsewhere', label: 'Somewhere else', icon: 'pin' },
              ]}
            />
            {where === 'airport' ? (
              <AirportPicker value={airports} onChange={setAirports} />
            ) : (
              <SpotPicker spot={spot} onSpot={setSpot} name={placeName} onName={setPlaceName} airports={airports} onAirports={setAirports} />
            )}
          </fieldset>

          {orgs.length ? (
            <Field label="Shoot for" hint={dataLine}>
              {(id) => (
                <Select id={id} value={ownerOrg ? ownerOrg.id : ''} onChange={(e) => setOrgPick(e.target.value || null)}>
                  <option value="">Just me (personal)</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </Select>
              )}
            </Field>
          ) : null}

          <Field label="Aircraft you’re filming" optional hint="Not known yet? Leave it blank. Positions appear when your aircraft data (the key you add in My settings) can see it. Add labeled aircraft, cameras and crew later in Shoot setup.">
            {(id) => (
              <TextInput
                id={id}
                mono
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                value={subject ?? ''}
                onChange={(e) => setSubject(normalizeRegistration(e.target.value))}
                placeholder="N123AB"
              />
            )}
          </Field>

          <Field label="Date" hint={`In ${timezone.replace(/_/g, ' ')}${multiDay ? `, to ${fmt.date(windowEnd, timezone)}` : ''}. Change the time zone, times or add more days in More details.`}>
            {(id) => <TextInput id={id} type="date" value={date} onChange={(e) => { setTimesTouched(true); changeDate(e.target.value); }} required />}
          </Field>
          {zoneHint ? (
            <Notice
              tone="warning"
              icon="clock"
              role="status"
              action={
                <Button size="sm" variant="secondary" onClick={showMore}>
                  Set the shoot’s time zone
                </Button>
              }
            >
              That location looks like it’s in a different time zone from {timezone.replace(/_/g, ' ')}.
            </Notice>
          ) : null}

          <Disclosure variant="card" className="form-more" detailsRef={moreRef} summary="More details" meta="Name, times, days, time zone, frame rate, notes">
            <div className="form-more__body">
              <Field label="Name" optional hint="Defaults to the aircraft (or place) + date.">
                {(id) => <TextInput id={id} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder={fallbackName} />}
              </Field>
              <div className="form-row">
                <Field label="Kind">
                  {(id) => (
                    <Select
                      id={id}
                      value={kind}
                      onChange={(e) => {
                        setKind(e.target.value as MissionKind);
                        setKindTouched(true);
                      }}
                    >
                      {MISSION_KINDS.map((k) => (
                        <option key={k.value} value={k.value}>
                          {k.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="Code" optional hint="Slate identifier, 2–24 letters, digits or dashes. Auto-generated if blank.">
                  {(id) => (
                    <TextInput id={id} mono value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))} placeholder="SHOOT-01" maxLength={24} />
                  )}
                </Field>
              </div>

              <fieldset className="form-section">
                <legend className="eyebrow">Window</legend>
                <div className="form-row">
                  <Field label="Start">{(id) => <TextInput id={id} type="time" value={startTime} onChange={(e) => { setTimesTouched(true); setStartTime(e.target.value); }} required />}</Field>
                  <Field label="End">{(id) => <TextInput id={id} type="time" value={endTime} onChange={(e) => { setTimesTouched(true); setEndTime(e.target.value); }} required />}</Field>
                </div>
                <Field label="Last day" optional hint="For a shoot over several days. Leave it on the first day for a single day (an end before the start runs past midnight).">
                  {(id) => <TextInput id={id} type="date" min={date} value={endDate ?? date} onChange={(e) => setEndDate(e.target.value && e.target.value !== date ? e.target.value : null)} />}
                </Field>
                <div className="form-row">
                  <Field label="Time zone" hint="Follows the airport or place you choose (North America); otherwise your device zone. Change it if the shoot runs on another clock.">
                    {(id) => <TimeZoneSelect id={id} data-tz value={timezone} onChange={(z) => { setZoneTouched(true); setTimezone(z); }} />}
                  </Field>
                  <Field label="Project frame rate">
                    {(id) => (
                      <Select id={id} value={frameRate} onChange={(e) => setFrameRate(e.target.value as FrameRate)}>
                        {FRAME_RATES.map((r) => (
                          <option key={r} value={r}>
                            {r.replace('DF', ' DF')} fps
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </div>
                <div className="window-preview num" data-testid="window-preview">
                  <span>
                    <span className="eyebrow">Local</span> {multiDay ? `${fmt.date(windowStart, timezone)} ` : ''}
                    {fmt.local(windowStart, timezone, false)} → {multiDay ? `${fmt.date(windowEnd, timezone)} ` : ''}
                    {fmt.local(windowEnd, timezone, false)}
                  </span>
                  <span>
                    <span className="eyebrow">UTC</span> {multiDay ? `${fmt.date(windowStart, 'UTC')} ` : ''}
                    {fmt.utc(windowStart, false)} → {multiDay ? `${fmt.date(windowEnd, 'UTC')} ` : ''}
                    {fmt.utc(windowEnd, false)}
                  </span>
                  <span>
                    <span className="eyebrow">Duration</span> {windowEnd > windowStart ? fmt.duration((windowEnd - windowStart) / 1000) : 'ends before it starts'}
                  </span>
                </div>
              </fieldset>

              <Field label="Notes" optional>
                {(id) => <TextArea id={id} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Objectives, shot list, contacts…" />}
              </Field>
            </div>
          </Disclosure>

          {error ? (
            <Notice tone="error" role="alert">
              {error}
            </Notice>
          ) : null}
          <div className="form-actions form-actions--sticky">
            <Button variant="ghost" onClick={() => nav('/missions')}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="lg" loading={busy} icon="check">
              Create shoot
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}

/**
 * The local zone for a place in the US (the common case for this product), from coarse longitude
 * bands. Null elsewhere, where the device zone and the "different time zone" hint still apply.
 */
export function zoneForPlace({ lat, lon }: LatLon): string | null {
  if (lat >= 18 && lat <= 23 && lon >= -161 && lon <= -154) return 'Pacific/Honolulu';
  if (lat >= 51 && lat <= 72 && lon >= -170 && lon <= -129) return 'America/Anchorage';
  if (lat < 24 || lat > 49.5 || lon < -125 || lon > -66) return null;
  if (lat >= 31.3 && lat <= 37 && lon >= -114.8 && lon <= -109.05) return 'America/Phoenix';
  if (lon <= -114.5) return 'America/Los_Angeles';
  if (lon <= -101.5) return 'America/Denver';
  if (lon <= -86.5) return 'America/Chicago';
  return 'America/New_York';
}

/** Somewhere other than an airport: a place name, the spot (my location or pasted coordinates), and optionally the airport the aircraft flies from. */
function SpotPicker({
  spot,
  onSpot,
  name,
  onName,
  airports,
  onAirports,
}: {
  spot: LatLon | null;
  onSpot(p: LatLon | null): void;
  name: string;
  onName(n: string): void;
  airports: PickedAirport[];
  onAirports(a: PickedAirport[]): void;
}) {
  const [text, setText] = useState(spot ? `${spot.lat.toFixed(5)}, ${spot.lon.toFixed(5)}` : '');
  const [locating, setLocating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bad = text.trim() !== '' && !parseLatLon(text);
  const here = async () => {
    setLocating(true);
    setErr(null);
    try {
      const fix = await getPhoneFix();
      onSpot({ lat: fix.lat, lon: fix.lon });
      setText(`${fix.lat.toFixed(5)}, ${fix.lon.toFixed(5)}`);
    } catch (e) {
      setErr(errorMessage(e).replace(/place the camera on the map/i, 'paste the coordinates'));
    } finally {
      setLocating(false);
    }
  };
  return (
    <div className="spot-picker">
      <Field label="Place name" optional hint="A camera site, ranch, beach, rooftop…">
        {(id) => <TextInput id={id} value={name} maxLength={60} onChange={(e) => onName(e.target.value)} placeholder="Ranch gate" />}
      </Field>
      <Field
        label="Coordinates"
        error={bad ? 'Use “latitude, longitude”, e.g. 36.6628, -121.6064' : (err ?? undefined)}
        hint="Use your location if you’re there, or paste coordinates from a maps app. Move it on the map later in Mission details."
      >
        {(id) => (
          <div className="spot-picker__where">
            <TextInput
              id={id}
              mono
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                onSpot(parseLatLon(e.target.value));
              }}
              placeholder="36.6628, -121.6064"
            />
            <Button icon="crosshair" loading={locating} onClick={() => void here()}>
              My location
            </Button>
          </div>
        )}
      </Field>
      <div>
        <div className="field__label">
          Airport the aircraft flies from <span className="field__optional">optional</span>
        </div>
        <AirportPicker value={airports} onChange={onAirports} autoFocus={false} emptyBody="Optional — the nearest airport with weather reports, or where the aircraft flies from." placeholder="Search airports — ICAO, name or city" />
      </div>
    </div>
  );
}

function AirportPicker({
  value,
  onChange,
  autoFocus = true,
  emptyBody = 'Search for the airport you’re at — add another if you’re at more than one.',
  placeholder,
}: {
  value: PickedAirport[];
  onChange(a: PickedAirport[]): void;
  autoFocus?: boolean;
  emptyBody?: string;
  placeholder?: string;
}) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Airport[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      abort.current?.abort();
      const ac = new AbortController();
      abort.current = ac;
      setLoading(true);
      try {
        setResults(await api.airports.search(q.trim(), ac.signal));
        setOpen(true);
      } catch {
        /* ignore */
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="airport-picker">
      {value.length ? (
        value.map((a, i) => (
          <div key={a.icao} className="airport-picker__current">
            <span className="airport-picker__pin" aria-hidden>
              <Icon name="pin" size={18} />
            </span>
            <div>
              <div className="airport-picker__icao num">{a.icao}</div>
              <div className="airport-picker__name">
                {a.name}
                {a.city ? ` · ${a.city}` : ''}
              </div>
            </div>
            {value.length > 1 && i === 0 ? (
              <Chip size="sm" tone="neutral" icon="cloud">
                Main
              </Chip>
            ) : null}
            <IconButton icon="close" size="sm" label={`Remove ${a.icao}`} onClick={() => onChange(value.filter((x) => x.icao !== a.icao))} />
          </div>
        ))
      ) : null}
      <div className="airport-picker__search">
        <TextInput
          icon="search"
          value={q}
          autoFocus={autoFocus}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder ?? (value.length ? 'Add another airport (optional)' : 'Search airports — ICAO, name or city')}
          aria-label="Search airports"
          autoComplete="off"
        />
        {loading ? (
          <span className="airport-picker__spin">
            <Spinner size={14} />
          </span>
        ) : null}
        {open && results.length ? (
          <ul className="airport-picker__results" role="listbox">
            {results.map((a) => (
              <li key={a.icao}>
                <button
                  type="button"
                  role="option"
                  aria-selected={value.some((x) => x.icao === a.icao)}
                  onClick={() => {
                    if (!value.some((x) => x.icao === a.icao)) onChange([...value, a]);
                    setOpen(false);
                    setQ('');
                  }}
                >
                  <span className="num">{a.icao}</span>
                  <span>{a.name}</span>
                  <span className="muted">{a.city}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {value.length ? null : <div className="field__hint">{emptyBody}</div>}
    </div>
  );
}
